import * as yup from 'yup';
import { TypedDocumentNode } from '@apollo/client';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddTicketMutation as AddTicket, DistanceUnit, GetTicketsQuery as GetTickets } from '__generated__/graphql';

import { getServerErrors, parseDistance } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddTicketMutation from './AddTicketMutation';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import UI from '../../../../common/UI';
import useCurrencyInput from '../../../../common/useCurrencyInput';
import useMutation from '../../../../api/useMutation';
import useNumberInput from '../../../../common/useNumberInput';
import useValidation from '../../../../common/useValidation';

type Ticket = AddTicket['addTicket'];
type Event = GetTickets['project']['event'];
type TicketCategory = Event['ticket_categories'][0];

const addTicketSchema = yup.object().shape({
  ticket_category: yup.object().required(),
  info: yup.object().shape({
    title: yup.string().required(),
  }),
});

export interface AddTicketFormProps {
  event: Event;
  ticketCategory?: TicketCategory;
  distanceUnits: DistanceUnit[];
  onSuccess?: (ticket: Ticket) => void;
  refetchQueries?: TypedDocumentNode[];
}

const AddTicketForm = ({
  event, ticketCategory, distanceUnits, onSuccess, refetchQueries = [GetEventLayoutQuery],
}: AddTicketFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const currencyInput = useCurrencyInput();
  const numberInput = useNumberInput();

  const distanceInputHandler = numberInput({ decimals: 3, max: 99_999_999 });

  const form = useForm({
    values: {
      ticket_category: ticketCategory
        ? { id: ticketCategory.id }
        : (event.ticket_categories.length === 1 ? { id: event.ticket_categories[0].id } : null),
      info: {
        title: '',
        distance: null,
        distance_unit: DistanceUnit.km,
      },
      pricing: {
        promotions: [{}],
        hide_on_invoice: false,
        allow_companies: true,
      },
    },
  });

  const [addTicket, { error, loading }] = useMutation(AddTicketMutation, {
    variables: {
      input: {
        event: {
          id: event.id,
        },
        ...form.values,
      },
    },
    onCompleted: ({ addTicket: ticket }) => {
      notifier.success(t('notifications.ticket_added'));
      onSuccess?.(ticket);
    },
    onError: () => {
      //
    },
    refetchQueries,
  });

  const { valid, errors } = useValidation({
    schema: addTicketSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const promotionKey = (prop: string) => `pricing.promotions[0].${prop}`;

  const handleTitleChange = (title: string) => {
    if (!form.values.info.distance || !form.getTouched('info.distance')) {
      const { distance, unit } = parseDistance(title);

      if (distance && unit) {
        form.set('info.distance', distance);
        form.set('info.distance_unit', unit);
      }
    }
  };

  return (
    <UI.Form onSubmit={() => addTicket()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_ticket')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_ticket_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        {event.ticket_categories.length > 1 && (
          <UI.InputGroup sc={{ valid: !errors.ticket_category, touched: form.touched.ticket_category }}>
            <UI.InputLabel htmlFor="ticket_category">
              {t('ticket_category')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.Select
              {...form.select('ticket_category.id', event.ticket_categories.map((ticketCategory) => ticketCategory.id))}
              sc={{ placeholder: !form.values.ticket_category }}
              id="ticket_category"
            >
              {!form.values.ticket_category && (
                <option value="">{t('select_ticket_category')}</option>
              )}
              {event.ticket_categories.map((ticketCategory, index) => (
                <option {...form.option(index)} key={index}>{ticketCategory.title}</option>
              ))}
            </UI.Select>
            <UI.ErrorMessages
              attribute={t('ticket_category')}
              errors={errors.ticket_category}
            />
          </UI.InputGroup>
        )}

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors['info.title'], touched: form.getTouched('info.title') }}>
            <UI.InputLabel htmlFor="ticket_title">
              {t('ticket_title')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('info.title', { onChange: handleTitleChange })}
              maxLength={50}
              id="ticket_title"
              placeholder={t('ticket_title_placeholder')}
              autoFocus={event.ticket_categories.length === 1}
            />
            <UI.ErrorMessages attribute={t('ticket_title')} errors={errors['info.title']} />
          </UI.InputGroup>

          <UI.InputGroup
            sc={{
              valid: !errors[promotionKey('amount')],
              touched: form.getTouched(promotionKey('amount')),
            }}
          >
            <UI.InputLabel htmlFor="selling_price">
              {t('selling_price')}
            </UI.InputLabel>
            <UI.CurrencyInput
              {...form.text(promotionKey('amount'), { inputHandler: {
                parse: currencyInput.parse,
                format: currencyInput.value,
              } })}
              id="selling_price"
            />
            <UI.ErrorMessages attribute={t('amount')} errors={errors[promotionKey('amount')]} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors['info.distance'], touched: form.getTouched('info.distance') }}>
            <UI.InputLabel htmlFor="distance">
              {t('distance')}
            </UI.InputLabel>
            <UI.Input
              {...form.text('info.distance', { inputHandler: distanceInputHandler })}
              maxLength={50}
              id="distance"
            />
            <UI.ErrorMessages attribute={t('distance')} errors={errors['info.distance']} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors['info.distance_unit'], touched: form.getTouched('info.distance_unit') }}>
            <UI.InputLabel htmlFor="distance_unit">
              {t('distance_unit')}
            </UI.InputLabel>
            <UI.Select
              {...form.select('info.distance_unit')}
              id="distance_unit"
            >
              {distanceUnits.map((unit) => (
                <option {...form.option(unit)} key={unit}>
                  {t(`common:distance_units.${unit}`)}
                </option>
              ))}
            </UI.Select>
            <UI.ErrorMessages attribute={t('distance_unit')} errors={errors['info.distance_unit']} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_ticket')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddTicketForm;
